import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PipelineStatusBanner } from '../components/PipelineStatusBanner';
import { Surface } from '../components/Surface';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type OverviewGroup = {
  socCode: string;
  label: string;
  localMedian: number | null;
  nationalMedian: number | null;
};

type WageOverview = {
  groups: OverviewGroup[];
  areaName: string | null;
  areaCode: string | null;
};

type AreaInfo = {
  areaCode: string | null;
  areaType: string | null;
  areaName: string | null;
  hasData: boolean;
  countyFips?: string | null;
  msaCode?: string | null;
  reason?: string;
};

type OccupationResult = {
  socCode: string;
  occupationTitle: string;
  areaCode: string;
  areaName: string;
  releaseYear: number;
};

type WageDistribution = {
  areaCode: string;
  areaName: string;
  areaType: string;
  socCode: string;
  occupationTitle: string;
  releaseYear: number;
  employmentEstimate: number | null;
  p10Wage: number | null;
  p25Wage: number | null;
  medianWage: number | null;
  meanWage: number | null;
  p75Wage: number | null;
  p90Wage: number | null;
  sourceUrl: string;
};

type WageTrendPoint = {
  releaseYear: number;
  medianWage: number | null;
  meanWage: number | null;
};

type WageBenchmarkResult = {
  local: WageDistribution;
  national: WageDistribution | null;
  localTrend: WageTrendPoint[];
  nationalTrend: WageTrendPoint[];
  citation: string;
};

const SOC_CATEGORIES = [
  { code: '11', label: 'Management' },
  { code: '13', label: 'Business & Financial' },
  { code: '15', label: 'Computer & Mathematical' },
  { code: '17', label: 'Architecture & Engineering' },
  { code: '19', label: 'Life & Physical Sciences' },
  { code: '21', label: 'Community & Social Service' },
  { code: '23', label: 'Legal' },
  { code: '25', label: 'Education & Library' },
  { code: '27', label: 'Arts & Media' },
  { code: '29', label: 'Healthcare Practitioners' },
  { code: '31', label: 'Healthcare Support' },
  { code: '33', label: 'Protective Service' },
  { code: '35', label: 'Food Preparation & Serving' },
  { code: '37', label: 'Building & Grounds' },
  { code: '39', label: 'Personal Care & Service' },
  { code: '41', label: 'Sales & Related' },
  { code: '43', label: 'Office & Admin Support' },
  { code: '45', label: 'Farming, Fishing & Forestry' },
  { code: '47', label: 'Construction & Extraction' },
  { code: '49', label: 'Installation & Repair' },
  { code: '51', label: 'Production' },
  { code: '53', label: 'Transportation & Moving' },
];

const BADGE_COLORS: Record<string, string> = {
  below_market: 'bg-amber-100 text-amber-800 border-amber-300',
  at_market: 'bg-green-100 text-green-800 border-green-300',
  above_market: 'bg-blue-100 text-blue-800 border-blue-300',
  premium: 'bg-purple-100 text-purple-800 border-purple-300',
};

const BADGE_THRESHOLDS = { below_market: 0.85, at_market: 1.15, above_market: 1.4 };

function computeBadge(implied: number, median: number): string {
  const r = implied / median;
  if (r < BADGE_THRESHOLDS.below_market) return 'below_market';
  if (r < BADGE_THRESHOLDS.at_market) return 'at_market';
  if (r < BADGE_THRESHOLDS.above_market) return 'above_market';
  return 'premium';
}

function BADGE_LABEL(b: string) {
  return (
    {
      below_market: 'Below Market',
      at_market: 'At Market',
      above_market: 'Above Market',
      premium: 'Premium',
    }[b] ?? b
  );
}

function fmt(
  n: number | null | undefined,
  opts?: { style?: string; minimumFractionDigits?: number },
) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: opts?.style === 'currency' ? 'currency' : 'decimal',
    currency: 'USD',
    minimumFractionDigits: opts?.minimumFractionDigits ?? 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function PercentileBar({
  dist,
  compareWage,
}: { dist: WageDistribution; compareWage?: number | null }) {
  const bars = [
    { label: 'P10', value: dist.p10Wage, color: '#94a3b8' },
    { label: 'P25', value: dist.p25Wage, color: '#64748b' },
    { label: 'Median', value: dist.medianWage, color: '#0f766e' },
    { label: 'Mean', value: dist.meanWage, color: '#0891b2' },
    { label: 'P75', value: dist.p75Wage, color: '#4f46e5' },
    { label: 'P90', value: dist.p90Wage, color: '#7c3aed' },
  ].filter((b) => b.value != null);

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={bars} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
          tick={{ fontSize: 11 }}
          width={52}
        />
        <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Annual wage']} />
        <Bar dataKey="value" radius={[3, 3, 0, 0]}>
          {bars.map((b, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Recharts Cell has no stable id
            <Cell key={i} fill={b.color} />
          ))}
        </Bar>
        {compareWage != null && (
          <ReferenceLine
            y={compareWage}
            stroke="#dc2626"
            strokeDasharray="4 2"
            label={{ value: 'Implied', position: 'right', fontSize: 10, fill: '#dc2626' }}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function WageBenchmarksRoute() {
  const getToken = useSessionTokenGetter();

  const [areaInfo, setAreaInfo] = useState<AreaInfo | null>(null);
  const [overview, setOverview] = useState<WageOverview | null>(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<OccupationResult[]>([]);
  const [selected, setSelected] = useState<OccupationResult | null>(null);
  const [result, setResult] = useState<WageBenchmarkResult | null>(null);
  const [impliedWage, setImpliedWage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load area info + overview once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      const [areaRes, overviewRes] = await Promise.all([
        fetch(`${API}/api/v1/wage-benchmarks/area`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/v1/wage-benchmarks/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (cancelled) return;
      if (areaRes.ok) setAreaInfo((await areaRes.json()) as AreaInfo);
      if (overviewRes.ok) setOverview((await overviewRes.json()) as WageOverview);
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // Debounced occupation search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = await getToken();
        const params = new URLSearchParams({ q: query.trim() });
        if (areaInfo?.areaCode) params.set('areaCode', areaInfo.areaCode);
        const res = await fetch(`${API}/api/v1/wage-benchmarks/search?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = (await res.json()) as { results: OccupationResult[] };
          setSuggestions(data.results);
        }
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [query, areaInfo, getToken]);

  async function loadDistribution(occ: OccupationResult) {
    if (!areaInfo?.areaCode) return;
    setSelected(occ);
    setSuggestions([]);
    setQuery(occ.occupationTitle);
    setResult(null);
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ socCode: occ.socCode, areaCode: areaInfo.areaCode });
      const res = await fetch(`${API}/api/v1/wage-benchmarks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setResult((await res.json()) as WageBenchmarkResult);
    } finally {
      setLoading(false);
    }
  }

  const implied = impliedWage ? Number.parseFloat(impliedWage.replace(/,/g, '')) : null;
  const badge =
    implied && result?.local?.medianWage ? computeBadge(implied, result.local.medianWage) : null;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Wage Benchmarks</h1>
        <p className="mt-1 text-sm text-slate-500">
          Compare occupational wages against BLS OES data for your configured geography.
        </p>
      </div>

      <PipelineStatusBanner />

      {/* Geography banner */}
      <Surface className="mb-6 p-4">
        {!areaInfo ? (
          <p className="text-sm text-slate-400">Loading geography…</p>
        ) : areaInfo.areaCode ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-800">
              {areaInfo.areaType === 'msa' ? 'MSA' : 'County'}
            </span>
            <span className="text-sm font-medium text-slate-800">
              {areaInfo.areaName ?? areaInfo.areaCode}
            </span>
            {!areaInfo.hasData && (
              <span className="ml-auto text-xs text-amber-600">
                No OES data loaded for this area yet — run the data pipeline first.
              </span>
            )}
          </div>
        ) : (
          <p className="text-sm text-amber-700">
            No geography configured. Add a County FIPS or MSA code to your default Jurisdiction
            Profile in{' '}
            <a href="/settings/jurisdiction" className="underline">
              Settings → Jurisdiction
            </a>
            .
          </p>
        )}
      </Surface>

      {/* Overview chart — local vs national median by major SOC group */}
      {!query && !selected && overview && overview.groups.length > 0 && (
        <Surface className="mb-6 p-5">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                Local vs National Median Wage by Occupation Group
              </h2>
              <p className="mt-0.5 text-xs text-slate-400">
                {overview.areaName} · Annual median wages · BLS OES
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={520}>
            <BarChart
              data={overview.groups}
              layout="vertical"
              margin={{ top: 4, right: 60, bottom: 4, left: 140 }}
              barCategoryGap="20%"
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 10 }}
                domain={[0, 'auto']}
              />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={136} />
              <Tooltip
                formatter={(v, name) => [
                  v != null ? `$${Number(v).toLocaleString()}` : '—',
                  name === 'localMedian' ? (overview.areaName ?? 'Local') : 'National',
                ]}
              />
              <Legend
                formatter={(value) =>
                  value === 'localMedian' ? (overview.areaName ?? 'Local') : 'National'
                }
                wrapperStyle={{ fontSize: 11 }}
              />
              <Bar
                dataKey="nationalMedian"
                fill="#cbd5e1"
                radius={[0, 3, 3, 0]}
                name="nationalMedian"
              />
              <Bar dataKey="localMedian" fill="#0d9488" radius={[0, 3, 3, 0]} name="localMedian" />
            </BarChart>
          </ResponsiveContainer>
        </Surface>
      )}

      {/* Occupation search */}
      <Surface className="mb-6 p-5">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
          Occupation search
        </label>
        <div className="relative">
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            placeholder="Search by occupation title or SOC code…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
              setResult(null);
            }}
            disabled={!areaInfo?.areaCode}
          />
          {searching && (
            <span className="absolute right-3 top-2.5 text-xs text-slate-400">searching…</span>
          )}
        </div>
        {suggestions.length > 0 && (
          <ul className="mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md">
            {suggestions.map((s) => (
              <li key={s.socCode}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => loadDistribution(s)}
                >
                  <span className="font-medium text-slate-800">{s.occupationTitle}</span>
                  <span className="ml-2 text-xs text-slate-400">{s.socCode}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Category directory — shown when search box is empty and no occupation selected */}
        {!query && !selected && areaInfo?.areaCode && (
          <div className="mt-3">
            <p className="mb-2 text-xs text-slate-400">Browse by category</p>
            <div className="flex flex-wrap gap-1.5">
              {SOC_CATEGORIES.map((cat) => (
                <button
                  key={cat.code}
                  type="button"
                  onClick={() => setQuery(cat.label)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800 transition-colors"
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </Surface>

      {loading && (
        <Surface className="p-8 text-center text-sm text-slate-400">Loading wage data…</Surface>
      )}

      {result && selected && (
        <>
          {/* Header */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">{selected.occupationTitle}</h2>
            <p className="text-xs text-slate-500">
              SOC {selected.socCode} · {result.local.areaName} · {result.local.releaseYear}
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Local distribution */}
            <Surface className="p-5">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Local — {result.local.areaName}
              </h3>
              <PercentileBar dist={result.local} compareWage={implied} />
              <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                {[
                  { label: 'P10', val: result.local.p10Wage },
                  { label: 'P25', val: result.local.p25Wage },
                  { label: 'Median', val: result.local.medianWage },
                  { label: 'Mean', val: result.local.meanWage },
                  { label: 'P75', val: result.local.p75Wage },
                  { label: 'P90', val: result.local.p90Wage },
                ].map(({ label, val }) => (
                  <div key={label} className="rounded bg-slate-50 p-2">
                    <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      {label}
                    </dt>
                    <dd className="mt-0.5 text-sm font-semibold text-slate-800">
                      {fmt(val, { style: 'currency' })}
                    </dd>
                  </div>
                ))}
              </dl>
            </Surface>

            {/* National comparison */}
            <Surface className="p-5">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                National comparison
              </h3>
              {result.national ? (
                <>
                  <PercentileBar dist={result.national} compareWage={implied} />
                  <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                    {[
                      { label: 'P10', val: result.national.p10Wage },
                      { label: 'P25', val: result.national.p25Wage },
                      { label: 'Median', val: result.national.medianWage },
                      { label: 'Mean', val: result.national.meanWage },
                      { label: 'P75', val: result.national.p75Wage },
                      { label: 'P90', val: result.national.p90Wage },
                    ].map(({ label, val }) => (
                      <div key={label} className="rounded bg-slate-50 p-2">
                        <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                          {label}
                        </dt>
                        <dd className="mt-0.5 text-sm font-semibold text-slate-800">
                          {fmt(val, { style: 'currency' })}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </>
              ) : (
                <p className="text-sm text-slate-400">
                  National OES data not available for this occupation.
                </p>
              )}
            </Surface>
          </div>

          {/* 3-year trend */}
          {(result.localTrend.length > 1 || result.nationalTrend.length > 1) &&
            (() => {
              // Merge local + national trend by year for the line chart
              const allYears = Array.from(
                new Set([
                  ...result.localTrend.map((p) => p.releaseYear),
                  ...result.nationalTrend.map((p) => p.releaseYear),
                ]),
              ).sort();
              const localByYear = new Map(
                result.localTrend.map((p) => [p.releaseYear, p.medianWage]),
              );
              const nationalByYear = new Map(
                result.nationalTrend.map((p) => [p.releaseYear, p.medianWage]),
              );
              const trendData = allYears.map((year) => ({
                year: String(year),
                local: localByYear.get(year) ?? null,
                national: nationalByYear.get(year) ?? null,
              }));
              return (
                <Surface className="mt-6 p-5">
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Median wage trend
                  </h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trendData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 11 }}
                        width={52}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip
                        formatter={(v, name) => [
                          v != null ? `$${Number(v).toLocaleString()}` : '—',
                          name === 'local' ? (result.local.areaName ?? 'Local') : 'National',
                        ]}
                      />
                      <Legend
                        formatter={(value) =>
                          value === 'local' ? (result.local.areaName ?? 'Local') : 'National'
                        }
                        wrapperStyle={{ fontSize: 11 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="national"
                        stroke="#cbd5e1"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="local"
                        stroke="#0d9488"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Surface>
              );
            })()}

          {/* Implied wage comparison */}
          <Surface className="mt-6 p-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Implied wage comparison (optional)
            </h3>
            <p className="mb-3 text-xs text-slate-500">
              Enter an implied annual wage (e.g. from a project's payroll / jobs) to see where it
              falls.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-56">
                <label className="sr-only">Implied annual wage</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-slate-400">$</span>
                  <input
                    type="number"
                    className="w-full rounded-md border border-slate-300 py-2 pl-6 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    placeholder="e.g. 75000"
                    value={impliedWage}
                    onChange={(e) => setImpliedWage(e.target.value)}
                    min={0}
                  />
                </div>
              </div>
              {badge && implied && result.local.medianWage && (
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${BADGE_COLORS[badge]}`}
                  >
                    {BADGE_LABEL(badge)}
                  </span>
                  <span className="text-sm text-slate-600">
                    {((implied / result.local.medianWage) * 100).toFixed(0)}% of local median
                  </span>
                </div>
              )}
            </div>
          </Surface>

          {/* Citation */}
          <p className="mt-4 text-[11px] text-slate-400">{result.citation}</p>
        </>
      )}
    </div>
  );
}
